/**
 * Entitlement / monetization model (Sprint 6B — UMXP).
 *
 * The playback platform CONSUMES entitlement decisions from backend services.
 * It does not implement billing, subscription, or ad-decision business logic.
 *
 * Models: Free | SVOD | TVOD | PVOD | AVOD (+ composites).
 */

export var MonetizationModel = Object.freeze({
  FREE: "free",
  SVOD: "svod",
  TVOD: "tvod",
  PVOD: "pvod",
  AVOD: "avod",
  UNKNOWN: "unknown",
});

export var EntitlementAction = Object.freeze({
  /** Viewer may play immediately */
  ALLOW: "allow",
  /** Hard gate — redirect to purchase / subscribe */
  REDIRECT: "redirect",
  /** Soft gate — show overlay / upgrade CTA but keep shell */
  SOFT_GATE: "soft_gate",
  /** Denied — no play path */
  DENY: "deny",
});

export var EntitlementRedirectKind = Object.freeze({
  PPV: "ppv",
  PAYMENT: "payment",
  SUBSCRIPTION: "subscription",
  NONE: "none",
});

/**
 * Ad break extension points (AVOD) — plugins subscribe; engine stays clean.
 */
export var AdBreakKind = Object.freeze({
  PRE_ROLL: "pre_roll",
  MID_ROLL: "mid_roll",
  POST_ROLL: "post_roll",
  SPONSOR_BUMPER: "sponsor_bumper",
  HOUSE_AD: "house_ad",
  DYNAMIC: "dynamic",
});

/**
 * @param {object} [partial]
 * @returns {object} entitlement snapshot
 */
export function createEntitlementSnapshot(partial) {
  partial = partial || {};
  return {
    model: partial.model || MonetizationModel.UNKNOWN,
    models: Array.isArray(partial.models)
      ? partial.models.slice()
      : partial.model
        ? [partial.model]
        : [],
    action: partial.action || EntitlementAction.ALLOW,
    redirectKind: partial.redirectKind || EntitlementRedirectKind.NONE,
    redirectPath: partial.redirectPath || null,
    /** Backend flags preserved for adapters */
    flags: Object.assign(
      {
        should_display_ppv: false,
        is_user_need_subscription: false,
        ppv_page_type: null,
        is_trailer: false,
      },
      partial.flags || {}
    ),
    /** Grace / expiry / ownership (filled by backend when available) */
    subscription: Object.assign(
      {
        active: null,
        gracePeriod: false,
        expiresAt: null,
        upgradeAvailable: false,
      },
      partial.subscription || {}
    ),
    purchase: Object.assign(
      {
        owned: null,
        rentalExpiresAt: null,
        restorable: false,
      },
      partial.purchase || {}
    ),
    ads: Object.assign(
      {
        enabled: false,
        breaks: [],
      },
      partial.ads || {}
    ),
    evaluatedAt: partial.evaluatedAt || Date.now(),
    source: partial.source || "backend",
  };
}

/**
 * Infer monetization model(s) from common backend video flags.
 * Prefer explicit `details.monetization` / `details.business_model` when present.
 *
 * @param {object} details video DTO
 * @param {object} [opts]
 * @returns {object} entitlement snapshot (action still decided by resolver)
 */
export function inferMonetizationFromDetails(details, opts) {
  opts = opts || {};
  details = details || {};
  var explicit =
    details.monetization ||
    details.business_model ||
    details.monetization_model ||
    null;
  var models = [];

  if (explicit) {
    var e = String(explicit).toLowerCase();
    var known = [
      MonetizationModel.FREE,
      MonetizationModel.SVOD,
      MonetizationModel.TVOD,
      MonetizationModel.PVOD,
      MonetizationModel.AVOD,
    ];
    if (known.indexOf(e) !== -1) {
      models.push(e);
    }
  }

  if (opts.isTrailer) {
    return createEntitlementSnapshot({
      model: MonetizationModel.FREE,
      models: [MonetizationModel.FREE],
      action: EntitlementAction.ALLOW,
      flags: { is_trailer: true },
    });
  }

  var needsSub = !!(
    details.is_user_need_subscription === true ||
    details.is_user_need_subscription === 1 ||
    details.is_user_need_subscription === "1"
  );
  var needsPpv = !!(
    details.should_display_ppv === true ||
    details.should_display_ppv === 1 ||
    details.should_display_ppv === "1"
  );
  var ppvType = details.ppv_page_type != null ? String(details.ppv_page_type) : null;

  if (needsSub) models.push(MonetizationModel.SVOD);
  if (needsPpv) {
    // PVOD when flagged as event/live premium; else TVOD
    var isPremiumEvent =
      !!details.is_live ||
      !!details.is_ppv_event ||
      (ppvType && /event|live|premium/i.test(ppvType));
    models.push(isPremiumEvent ? MonetizationModel.PVOD : MonetizationModel.TVOD);
  }
  if (details.avod || details.ads_enabled || details.has_ads) {
    models.push(MonetizationModel.AVOD);
  }
  if (!models.length) {
    models.push(MonetizationModel.FREE);
  }

  return createEntitlementSnapshot({
    model: models[0],
    models: models,
    flags: {
      should_display_ppv: needsPpv,
      is_user_need_subscription: needsSub,
      ppv_page_type: ppvType,
      is_trailer: !!opts.isTrailer,
    },
  });
}

export default {
  MonetizationModel: MonetizationModel,
  EntitlementAction: EntitlementAction,
  EntitlementRedirectKind: EntitlementRedirectKind,
  AdBreakKind: AdBreakKind,
  createEntitlementSnapshot: createEntitlementSnapshot,
  inferMonetizationFromDetails: inferMonetizationFromDetails,
};
